import React, { useState, useEffect, useContext } from 'react';
import { WindowSizeContext } from '/src/App';
import './style.css';
import { Scatter } from 'react-chartjs-2';
import ProjectsListCard from '/src/Components/ProjectsListCard/ProjectsListCard';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    annotationPlugin
);

const ComparisonChart = ({
    projects,
    multiProjectsFilterOne,
    multiProjectsFilterTwo,
    hoveredProjectId,
    setHoveredProjectId
}) => {
    const { deviceType } = useContext(WindowSizeContext);
    // console.log(projects);
    const [displayVisualType, setDisplayVisualType] = useState(null);
    const [datasetOne, setDatasetOne] = useState(null);
    const [datasetTwo, setDatasetTwo] = useState(null);
    const [scatterChartData, setScatterChartData] = useState(null);
    const [isChartHovered, setIsChartHovered] = useState(false);
    const [displayProjectCard, setDisplayProjectCard] = useState(false);
    const [hoveredProject, setHoveredProject] = useState(null);
    const [chartLabels, setChartLabels] = useState(null);
    const [chartFontSize, setChartFontSize] = useState(12);

    useEffect(() => {
        if (deviceType === 'mobile') {
            setChartFontSize(10);
        } else if (deviceType === 'tablet') {
            setChartFontSize(11);
        } else {
            setChartFontSize(12);
        }
    }, [deviceType]);

    // Return an array of objects that takes the id and name of the project and then the value of the key
    const pullData = (projects, key, label, dataType) => {
        return projects.map(project => ({
            id: project.project_id,
            name: project.project_name,
            value: project[key],
            label: label,
            color: project.color,
            dataType: dataType
        }));
    }

    useEffect(() => {

        // Reset all of the datasets and chart type
        setDatasetOne(null);
        setDatasetTwo(null);
        setScatterChartData(null);
        setDisplayVisualType(null);
        setChartLabels(null);
        
        // First set the display visual type based on the filters
        const isSelectionOneAFilter = multiProjectsFilterOne.type === 'filter';
        const isSelectionTwoAFilter = multiProjectsFilterTwo.type === 'filter';

        let visualType = 'chart';
        if (isSelectionOneAFilter && isSelectionTwoAFilter) {
            visualType = 'quad';
        } else if (!isSelectionOneAFilter && !isSelectionTwoAFilter) {
            visualType = 'chart';
        } else {
            visualType = 'column';
        }
        setDisplayVisualType(visualType);

        // Then pull the data for the datasets
        const areThereProjects = projects && projects.length > 0;
        const isFilterOnePresent = multiProjectsFilterOne && multiProjectsFilterOne.datapoint && multiProjectsFilterOne.label;
        const isFilterTwoPresent = multiProjectsFilterTwo && multiProjectsFilterTwo.datapoint && multiProjectsFilterTwo.label;
        const areThereFilters = isFilterOnePresent && isFilterTwoPresent;

        if (areThereProjects && areThereFilters) {
            if (visualType === 'chart' || visualType === 'quad') {

                const xAxisLabel = multiProjectsFilterOne.chartLabel;
                const yAxisLabel = multiProjectsFilterTwo.chartLabel;

                const chartsLabelObject = {
                    x_axis: xAxisLabel,
                    y_axis: yAxisLabel,
                }

                if (visualType === 'quad') {
                    chartsLabelObject.x_left = multiProjectsFilterOne.columnOneName;
                    chartsLabelObject.x_right = multiProjectsFilterOne.columnTwoName;
                    chartsLabelObject.y_top = multiProjectsFilterTwo.columnOneName;
                    chartsLabelObject.y_bottom = multiProjectsFilterTwo.columnTwoName;
                }

                setChartLabels(chartsLabelObject);

                const datasetOne = pullData(projects, multiProjectsFilterOne.datapoint, multiProjectsFilterOne.label, multiProjectsFilterOne.dataType);
                const datasetTwo = pullData(projects, multiProjectsFilterTwo.datapoint, multiProjectsFilterTwo.label, multiProjectsFilterTwo.dataType);
                setDatasetOne(datasetOne);
                setDatasetTwo(datasetTwo);

            } else if (visualType == 'column') {

                let datasetToUseAsFilter = multiProjectsFilterOne;
                let datasetToUseAsValue = multiProjectsFilterTwo;

                if (isSelectionTwoAFilter) {
                    datasetToUseAsFilter = multiProjectsFilterTwo;
                    datasetToUseAsValue = multiProjectsFilterOne;
                }

                const xAxisLabel = datasetToUseAsFilter.chartLabel;
                const columnOneLabel = datasetToUseAsFilter.columnOneName;
                const columnTwoLabel = datasetToUseAsFilter.columnTwoName;

                const yAxisLabel = datasetToUseAsValue.chartLabel;

                setChartLabels({
                    x_axis: xAxisLabel,
                    x_left: columnOneLabel,
                    x_right: columnTwoLabel,
                    y_axis: yAxisLabel,
                })

                const projectIdsFromFilterTrue = projects.filter(project => project[datasetToUseAsFilter.datapoint]).map(project => project.project_id);
                const projectIdsFromFilterFalse = projects.filter(project => !project[datasetToUseAsFilter.datapoint]).map(project => project.project_id);

                const projectsMatchingIdsTrue = projects.filter(project => projectIdsFromFilterTrue.includes(project.project_id));
                const projectsMatchingIdsFalse = projects.filter(project => projectIdsFromFilterFalse.includes(project.project_id));

                const datasetOne = pullData(projectsMatchingIdsTrue, datasetToUseAsValue.datapoint, datasetToUseAsValue.label, datasetToUseAsValue.dataType);
                const datasetTwo = pullData(projectsMatchingIdsFalse, datasetToUseAsValue.datapoint, datasetToUseAsValue.label, datasetToUseAsValue.dataType);

                setDatasetOne(datasetOne);
                setDatasetTwo(datasetTwo);
            }

        }
    }, [projects, multiProjectsFilterOne, multiProjectsFilterTwo]);

    useEffect(() => {
        if (isChartHovered && hoveredProjectId) {
            setDisplayProjectCard(true);
            const hoveredProject = projects.find(project => project.project_id === hoveredProjectId);
            if (hoveredProject) {
                setHoveredProject(hoveredProject);
            }
        } else {
            setDisplayProjectCard(false);
            setHoveredProject(null);
        }
    }, [isChartHovered, hoveredProjectId]);

    // Add this helper function
    const formatValue = (value, dataType) => {
        if (!value && value !== 0) return '';

        switch (dataType) {
            case 'dollar':
                if (Math.abs(value) >= 1000) {
                    return `$${Math.round(value / 1000)}K`;
                }
                return `$${Math.round(value)}`;

            case 'number':
                return Math.round(value).toString();

            case 'roi':
                return `${Math.round(value)}x`;

            default:
                return value;
        }
    };

    const regularChartOptions = {
        scales: {
            x: {
                type: 'linear',
                title: {
                    display: true,
                    text: datasetOne?.[0]?.label || '',
                    font: {
                        size: chartFontSize,
                        weight: 'bold'
                    }
                },
                grid: {
                    display: true
                },
                ticks: {
                    font: {
                        size: 10
                    },
                    callback: function (value) {
                        return formatValue(value, datasetOne?.[0]?.dataType);
                    }
                }
            },
            y: {
                type: 'linear',
                title: {
                    display: true,
                    text: datasetTwo?.[0]?.label || '',
                    font: {
                        size: chartFontSize,
                        weight: 'bold'
                    }
                },
                grid: {
                    display: true
                },
                ticks: {
                    font: {
                        size: 10
                    },
                    callback: function (value) {
                        return formatValue(value, datasetTwo?.[0]?.dataType);
                    }
                }
            }
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    title: function (context) {
                        return context[0].dataset.label;
                    },
                    label: function (context) {
                        const xLabel = datasetOne?.[0]?.label;
                        const yLabel = datasetTwo?.[0]?.label;
                        const xValue = formatValue(context.raw.x, datasetOne?.[0]?.dataType);
                        const yValue = formatValue(context.raw.y, datasetTwo?.[0]?.dataType);
                        return `${xLabel}: ${xValue}\n${yLabel}: ${yValue}`;
                    }
                }
            },
            datalabels: {
                display: false
            }
        },
        maintainAspectRatio: false,
        onHover: (event, elements) => {
            if (elements && elements.length > 0) {
                console.log(elements);
                const datasetIndex = elements[0].datasetIndex;
                const pointData = datasetOne[datasetIndex];
                const projectId = pointData?.id || null;

                if (projectId) {
                    setHoveredProjectId(projectId);
                }
            } else {
                setHoveredProjectId(null);
            }
        },
        hover: {
            mode: 'nearest',
            intersect: true
        }
    };

    const getScatterData = () => {
        if (!datasetOne || !datasetTwo) return { datasets: [] };


        const dataset = datasetOne.map((item, index) => ({
            label: item.name,
            data: [{
                x: parseFloat(item.value),
                y: parseFloat(datasetTwo[index].value)
            }],
            backgroundColor: (hoveredProjectId && hoveredProjectId !== item.id) ? item.color + '10' : item.color,
            borderColor: (hoveredProjectId && hoveredProjectId === item.id) ? "black" : item.color + '10',
            pointRadius: 8,
            pointStyle: 'circle',
            showLine: false,
            pointHoverRadius: 10
        }));

        return { datasets: dataset };
    };

    const getColumnsData = () => {
        if (!datasetOne || !datasetTwo) return { datasets: [] };

        const jitterWidth = 0.3; // How much spread we want
        
        const addJitter = (baseX) => {
            return baseX + (Math.random() - 0.5) * jitterWidth;
        };

        return {
            datasets: [
                ...datasetOne.map(item => ({
                    label: item.name,
                    data: [{
                        x: addJitter(1), // Add jitter around x=1
                        y: parseFloat(item.value),
                    }],
                    backgroundColor: (hoveredProjectId && hoveredProjectId !== item.id) ? item.color + '10' : item.color,
                    borderColor: (hoveredProjectId && hoveredProjectId === item.id) ? "black" : item.color + '10',
                    pointRadius: 8,
                    pointStyle: 'circle',
                    showLine: false,
                    pointHoverRadius: 10
                })),
                ...datasetTwo.map(item => ({
                    label: item.name,
                    data: [{
                        x: addJitter(3), // Add jitter around x=3
                        y: parseFloat(item.value),
                    }],
                    backgroundColor: (hoveredProjectId && hoveredProjectId !== item.id) ? item.color + '10' : item.color,
                    borderColor: (hoveredProjectId && hoveredProjectId === item.id) ? "black" : item.color + '10',
                    pointRadius: 8,
                    pointStyle: 'circle',
                    showLine: false,
                    pointHoverRadius: 10
                }))
            ]
        };
    };

    useEffect(() => {
        if (datasetOne && datasetTwo) {
            if (displayVisualType === 'chart') {
                setScatterChartData(getScatterData());
            } else if (displayVisualType === 'column') {
                setScatterChartData(getColumnsData());
            }
        }
    }, [hoveredProjectId, datasetOne, datasetTwo]);

 

    // Add this new options object
    const columnChartOptions = {
        scales: {
            x: {
                type: 'linear',
                title: {
                    display: true,
                    text: chartLabels?.x_axis || '',
                    font: {
                        size: chartFontSize,
                        weight: 'bold'
                    }
                },
                grid: {
                    display: true,
                    drawOnChartArea: true,
                    color: (context) => {
                        return context.tick.value === 2 ? '#666' : 'transparent';
                    },
                    lineWidth: (context) => {
                        return context.tick.value === 2 ? 1 : 0;
                    }
                },
                ticks: {
                    callback: function(value) {
                        if (value === 1) return chartLabels?.x_left || '';
                        if (value === 3) return chartLabels?.x_right || '';
                        return '';
                    },
                    autoSkip: false,
                    rotation: 0,
                    maxRotation: 0,  // Force no rotation
                    minRotation: 0,  // Force no rotation
                    align: 'center', // Center align the labels
                    font: {
                        size: chartFontSize
                    }
                },
                min: 0,
                max: 4,
                border: {
                    display: true
                }
            },
            y: {
                type: 'linear',
                title: {
                    display: true,
                    text: chartLabels?.y_axis || '',
                    font: {
                        size: chartFontSize,
                        weight: 'bold'
                    }
                },
                grid: {
                    display: true
                },
                ticks: {
                    font: {
                        size: chartFontSize
                    },
                    callback: function(value) {
                        return formatValue(value, datasetOne?.[0]?.dataType);
                    }
                }
            }
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                mode: 'nearest',
                intersect: true,
                callbacks: {
                    title: function(context) {
                        return context[0].dataset.label;
                    },
                    label: function(context) {
                        const columnLabel = Math.abs(context.raw.x - 1) < 1 ? chartLabels?.x_left : chartLabels?.x_right;
                        const value = formatValue(context.raw.y, datasetOne?.[0]?.dataType);
                        return `${columnLabel}\n${chartLabels?.y_axis}: ${value}`;
                    }
                }
            },
            datalabels: {
                display: false
            }
        },
        maintainAspectRatio: false,
        onHover: (event, elements) => {
            if (elements && elements.length > 0) {
                const element = elements[0];
                const isLeftColumn = Math.abs(element.element.x - 1) < 1;
                const dataset = isLeftColumn ? datasetOne : datasetTwo;
                const index = isLeftColumn ? element.datasetIndex : element.datasetIndex - datasetOne.length;
                const pointData = dataset?.[index];
                
                if (pointData?.id) {
                    setHoveredProjectId(pointData.id);
                }
            } else {
                setHoveredProjectId(null);
            }
        },
        hover: {
            mode: 'nearest',
            intersect: true
        }
    };

    const getQuadrantData = () => {
        if (!datasetOne || !datasetTwo) return { datasets: [] };

        // Helper function to arrange points in grid within each quadrant
        const arrangePointsInGrid = (points, quadrantX, quadrantY) => {
            // Adjust padding to center points more
            const GRID_PADDING = 1.5; // Increased padding from edges
            const quadrantWidth = 5;
            const quadrantHeight = 5;
            
            const pointsCount = points.length;
            const gridSize = Math.ceil(Math.sqrt(pointsCount));
            
            return points.map((point, index) => {
                const row = Math.floor(index / gridSize);
                const col = index % gridSize;
                
                // Calculate spacing to center the grid
                const xSpacing = (quadrantWidth - (2 * GRID_PADDING)) / Math.max(gridSize - 1, 1);
                const ySpacing = (quadrantHeight - (2 * GRID_PADDING)) / Math.max(gridSize - 1, 1);
                
                // Center the grid in the quadrant
                const xOffset = quadrantX + GRID_PADDING;
                const yOffset = quadrantY + GRID_PADDING;
                
                // Calculate actual coordinates
                const x = xOffset + (col * xSpacing);
                const y = yOffset + (row * ySpacing);
                
                return {
                    ...point,
                    x: x,
                    y: y
                };
            });
        };

        // Group projects by quadrant
        const quadrants = {
            q1: [], // top-left
            q2: [], // top-right
            q3: [], // bottom-left
            q4: []  // bottom-right
        };

        projects.forEach(project => {
            const isXTrue = project[multiProjectsFilterOne.datapoint];
            const isYTrue = project[multiProjectsFilterTwo.datapoint];
            
            const point = {
                project_id: project.project_id,
                color: project.color,
                name: project.project_name
            };

            if (!isXTrue && isYTrue) quadrants.q1.push(point);
            else if (isXTrue && isYTrue) quadrants.q2.push(point);
            else if (!isXTrue && !isYTrue) quadrants.q3.push(point);
            else quadrants.q4.push(point);
        });

        const q1Points = arrangePointsInGrid(quadrants.q1, 0, 5);
        const q2Points = arrangePointsInGrid(quadrants.q2, 5, 5);
        const q3Points = arrangePointsInGrid(quadrants.q3, 0, 0);
        const q4Points = arrangePointsInGrid(quadrants.q4, 5, 0);

        const allPoints = [...q1Points, ...q2Points, ...q3Points, ...q4Points];
        
        const dataset = allPoints.map(point => ({
            projectId: point.project_id,
            label: point.name,
            data: [{
                x: point.x,
                y: point.y
            }],
            backgroundColor: (hoveredProjectId && hoveredProjectId !== point.project_id) ? point.color + '10' : point.color,
            borderColor: (hoveredProjectId && hoveredProjectId === point.project_id) ? "black" : point.color + '10',
            pointRadius: 8,
            pointStyle: 'circle',
            showLine: false,
            pointHoverRadius: 10,
            datalabels: {
                display: false
            }
        }));

        return { datasets: dataset };
    };

    useEffect(() => {
        if (datasetOne && datasetTwo) {
            if (displayVisualType === 'chart') {
                setScatterChartData(getScatterData());
            } else if (displayVisualType === 'column') {
                setScatterChartData(getColumnsData());
            } else if (displayVisualType === 'quad') {
                setScatterChartData(getQuadrantData());
            }
        }
    }, [hoveredProjectId, datasetOne, datasetTwo]);

    console.log(scatterChartData);

    const handleChartEnter = () => {
        setIsChartHovered(true);
    }
    const handleChartLeave = () => {
        setIsChartHovered(false);
        setHoveredProjectId(null);
    }

    // Add this new options object
    const quadrantChartOptions = {
        scales: {
            x: {
                type: 'linear',
                min: 0,
                max: 10,
                grid: {
                    drawTicks: false,
                    color: (context) => context.tick.value === 5 ? '#666' : 'transparent'
                },
                title: {
                    display: false
                },
                ticks: { 
                    display: false,
                    callback: function() { return ''; }
                },
                display: true,
                border: {
                    display: false // Hide the axis line since we'll use the border annotation
                }
            },
            y: {
                type: 'linear',
                min: 0,
                max: 10,
                grid: {
                    drawTicks: false,
                    color: (context) => context.tick.value === 5 ? '#666' : 'transparent'
                },
                title: {
                    display: false
                },
                ticks: { 
                    display: false,
                    callback: function() { return ''; }
                },
                display: true,
                border: {
                    display: false // Hide the axis line since we'll use the border annotation
                }
            }
        },
        plugins: {
            legend: {
                display: false
            },
            datalabels: {
                display: false
            },
            annotation: {
                annotations: {
                    chartBorder: {
                        type: 'box',
                        xMin: 0,
                        xMax: 10,
                        yMin: 0,
                        yMax: 10,
                        borderColor: 'var(--page_background)',
                        borderWidth: 2,
                        backgroundColor: 'transparent'
                    },
                    quadrant1: {
                        type: 'box',
                        xMin: 0,
                        xMax: 5,
                        yMin: 5,
                        yMax: 10,
                        backgroundColor: 'rgba(255, 245, 157, 0.1)',
                        borderColor: 'transparent',
                        label: {
                            display: true,
                            content: chartLabels?.x_left + ' & ' + chartLabels?.y_top,
                            position: {
                                x: 'start',
                                y: 'end'
                            },
                            xAdjust: 10,
                            yAdjust: -10,
                            font: {
                                size: 12,
                                weight: 'bold'
                            }
                        }
                    },
                    quadrant2: {
                        type: 'box',
                        xMin: 5,
                        xMax: 10,
                        yMin: 5,
                        yMax: 10,
                        backgroundColor: 'rgba(165, 214, 167, 0.1)',
                        borderColor: 'transparent',
                        label: {
                            display: true,
                            content: chartLabels?.x_right + ' & ' + chartLabels?.y_top,
                            position: {
                                x: 'end',
                                y: 'end'
                            },
                            xAdjust: -10,
                            yAdjust: -10,
                            font: {
                                size: 12,
                                weight: 'bold'
                            }
                        }
                    },
                    quadrant3: {
                        type: 'box',
                        xMin: 0,
                        xMax: 5,
                        yMin: 0,
                        yMax: 5,
                        backgroundColor: 'rgba(255, 174, 201, 0.1)',
                        borderColor: 'transparent',
                        label: {
                            display: true,
                            content: chartLabels?.x_left + ' & ' + chartLabels?.y_bottom,
                            position: {
                                x: 'start',
                                y: 'start'
                            },
                            xAdjust: 10,
                            yAdjust: 10,
                            font: {
                                size: 12,
                                weight: 'bold'
                            }
                        }
                    },
                    quadrant4: {
                        type: 'box',
                        xMin: 5,
                        xMax: 10,
                        yMin: 0,
                        yMax: 5,
                        backgroundColor: 'rgba(159, 168, 218, 0.1)',
                        borderColor: 'transparent',
                        label: {
                            display: true,
                            content: chartLabels?.x_right + ' & ' + chartLabels?.y_bottom,
                            position: {
                                x: 'end',
                                y: 'start'
                            },
                            xAdjust: -10,
                            yAdjust: 10,
                            font: {
                                size: 12,
                                weight: 'bold'
                            }
                        }
                    }
                }
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    title: function(context) {
                        return context[0].dataset.label;
                    },
                    label: function(context) {
                        const xValue = context.raw.x > 5 ? chartLabels?.x_right : chartLabels?.x_left;
                        const yValue = context.raw.y > 5 ? chartLabels?.y_top : chartLabels?.y_bottom;
                        return `${xValue} & ${yValue}`;
                    }
                }
            }
        },
        maintainAspectRatio: false,
        onHover: (event, elements) => {
            if (elements && elements.length > 0) {
                const projectIdToUse = elements[0].element.$context.element.$datalabels[0].$context.dataset.projectId;
                setHoveredProjectId(projectIdToUse);
            } else {
                setHoveredProjectId(null);
            }
        }
    };

    return (
        <div
            className={`comparison-chart ${displayProjectCard ? 'project-card-displayed' : ''}`}
            onMouseEnter={handleChartEnter}
            onMouseLeave={handleChartLeave}
        >
            {/* <h1>Comparison Chart</h1>
            <p>Displaying {displayVisualType} visual</p> */}
            {datasetOne && datasetTwo && scatterChartData && (
                <>
                    {(['chart', 'column', 'quad'].includes(displayVisualType)) && (
                        <>
                            <div className="scatter-chart-container">
                                <Scatter
                                    data={scatterChartData}
                                    options={
                                        displayVisualType === 'chart' ? regularChartOptions :
                                        displayVisualType === 'column' ? columnChartOptions :
                                        quadrantChartOptions
                                    }
                                    height={400}
                                />
                            </div>
                            {/* {displayProjectCard && hoveredProject && (
                        <ProjectsListCard key={hoveredProject.project_id} project={hoveredProject} />
                        )} */}
                        </>
                    )}
                </>
            )}
        </div>
    )
}

export default ComparisonChart;